"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoomMonitor = void 0;
const rxjs_1 = require("rxjs");
const amazon_chime_sdk_js_1 = require("amazon-chime-sdk-js");
const streem_logger_1 = require("./util/streem_logger");
const get_video_metrics_for_remote_user_1 = require("./util/get_video_metrics_for_remote_user");
class RoomMonitor {
    constructor(attendeeId, connectionStateObservable, isLocalAudioTrackEnabledObservable, isLocalVideoTrackEnabledObservable, chimeObserver) {
        this.LOW_FRAME_RATE_THRESHOLD = 2;
        this.logger = streem_logger_1.streemLogger.extend('sdk-chime-video:RoomMonitor');
        this.roomWebRtcStatsSubject = new rxjs_1.Subject();
        this.roomWebRtcStats = this.roomWebRtcStatsSubject.asObservable();
        this.isLocalVideoFrozenSubject = new rxjs_1.Subject();
        this.isLocalVideoFrozen$ = this.isLocalVideoFrozenSubject.asObservable();
        this.updateRoomStats = (clientMetricReport) => __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.connectionState != 'CONNECTED') {
                    this.logger.debug('Skipping metrics as connection state is not CONNECTED:' + this.connectionState);
                    return;
                }
                if (!clientMetricReport) {
                    this.logger.warn('client metric report is not received/defined');
                    return;
                }
                // We're tracking the enabled state for the local audio and video tracks because it impacts the values of
                // downstream and upstream metrics
                this.isLocalAudioTrackEnabledSubscription = this.isLocalAudioTrackEnabledObservable
                    .pipe((0, rxjs_1.take)(1))
                    .subscribe(isLocalAudioTrackEnabled => {
                    this.isLocalAudioTrackEnabled = isLocalAudioTrackEnabled;
                });
                this.isLocalVideoTrackEnabledSubscription = this.isLocalVideoTrackEnabledObservable
                    .pipe((0, rxjs_1.take)(1))
                    .subscribe(isLocalVideoTrackEnabled => {
                    this.isLocalVideoTrackEnabled = isLocalVideoTrackEnabled;
                });
                const webRtcStatsReport = this.buildWebRtcStatsReport(clientMetricReport);
                this.roomWebRtcStatsSubject.next(webRtcStatsReport);
                this.logger.debug(`webRtcStatsReport:${JSON.stringify(webRtcStatsReport)}`);
                const isCurrentLocalVideoFrozen = this.isVideoFrozen(clientMetricReport);
                this.isLocalVideoFrozenSubject.next(isCurrentLocalVideoFrozen);
            }
            catch (e) {
                this.logger.error('Room monitor could not get room stats: ', e);
            }
        });
        this.attendeeId = attendeeId;
        this.connectionStateSubscription = connectionStateObservable
            .pipe((0, rxjs_1.take)(1))
            .subscribe(state => {
            this.connectionState = state;
        });
        this.isLocalAudioTrackEnabledObservable = isLocalAudioTrackEnabledObservable;
        this.isLocalVideoTrackEnabledObservable = isLocalVideoTrackEnabledObservable;
        this.chimeObserver = chimeObserver;
        this.chimeObserver.addMetricsObserver(this.updateRoomStats);
        this.logger.info('Room Monitor connected');
    }
    disconnect() {
        this.connectionStateSubscription.unsubscribe();
        this.isLocalAudioTrackEnabledSubscription.unsubscribe();
        this.isLocalVideoTrackEnabledSubscription.unsubscribe();
        this.chimeObserver.removeMetricsObserver(this.updateRoomStats);
        this.logger.info('Room monitor disconnected');
    }
    buildWebRtcStatsReport(clientMetricReport) {
        const localAudioTrackStats = [];
        const localVideoTrackStats = [];
        const remoteAudioTrackStats = [];
        const remoteVideoTrackStats = [];
        let peerConnectionId = '';
        clientMetricReport.getRTCStatsReport().forEach(stats => {
            if (stats.type === 'peer-connection') {
                peerConnectionId = stats.id;
            }
        });
        if (Object.keys(clientMetricReport.streamMetricReports).length > 0) {
            const observableMetrics = clientMetricReport.getObservableMetrics();
            const observableVideoMetrics = clientMetricReport.getObservableVideoMetrics();
            if (observableMetrics && Object.keys(observableMetrics).length > 0) {
                localAudioTrackStats.push(this.buildLocalAudioTrackStats(clientMetricReport, observableMetrics));
                remoteAudioTrackStats.push(this.buildRemoteAudioTrackStats(clientMetricReport, observableMetrics));
            }
            if (observableVideoMetrics && Object.keys(observableVideoMetrics).length > 0) {
                const localVideoTrackWebRtcStats = this.buildLocalVideoTrackStats(clientMetricReport, observableVideoMetrics, this.attendeeId);
                if (localVideoTrackWebRtcStats) {
                    localVideoTrackStats.push(localVideoTrackWebRtcStats);
                }
                const remoteVideoTrackWebRtcStats = this.buildRemoteVideoTrackStats(clientMetricReport, observableVideoMetrics, this.attendeeId, this.logger);
                if (remoteVideoTrackWebRtcStats) {
                    remoteVideoTrackStats.push(remoteVideoTrackWebRtcStats);
                }
            }
        }
        return {
            peerConnectionId,
            localAudioTrackStats,
            localVideoTrackStats,
            remoteAudioTrackStats,
            remoteVideoTrackStats,
        };
    }
    /* LocalAudioTrackStats
     * -- audioLevel: number | null;
     * -- bytesSent: number | null;
     * -- bitRate: number | null;
     * -- packetsSent: number | null;
     * -- roundTripTime: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- enabled?: boolean;
     * -- trackId: string; // Might not be available in Chime
     * -- trackSid: string; // Might not be available in Chime
     */
    buildLocalAudioTrackStats(clientMetricReport, observableMetrics) {
        return {
            audioLevel: observableMetrics.audioUpstreamLevel,
            bytesSent: this.getTrackBytesTransmitted(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            bitRate: this.getAudioTrackBitRate(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            packetsSent: observableMetrics.audioPacketsSent,
            roundTripTime: observableMetrics.audioUpstreamRoundTripTimeMs,
            jitter: observableMetrics.audioUpstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            packetLossPercentage: observableMetrics.audioPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).audioCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            enabled: this.isLocalAudioTrackEnabled,
            trackId: '',
            trackSid: '',
        };
    }
    /* LocalVideoTrackStats
     * -- captureDimensions: VideoTrackDimensions | null;
     * -- captureFrameRate: number | null;
     * -- dimensions: VideoTrackDimensions | null;
     * -- frameRate: number | null;
     * -- bytesSent: number | null;
     * -- bitRate: number | null;
     * -- packetsSent: number | null;
     * -- roundTripTime: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- ssrc: string;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- enabled?: boolean;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     */
    buildLocalVideoTrackStats(clientMetricReport, observableVideoMetrics, attendeeId) {
        var _a, _b, _c;
        const videoUpstreamSsrc = this.getStreamSsrc(clientMetricReport.streamMetricReports, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM);
        if (!videoUpstreamSsrc) {
            return null;
        }
        const upstreamObservableVideoMetrics = observableVideoMetrics[attendeeId][videoUpstreamSsrc];
        if (!upstreamObservableVideoMetrics) {
            return null;
        }
        const videoSourceStats = this.parseWebRtcStatsReportForVideoSourceStats(clientMetricReport.getRTCStatsReport());
        return {
            captureDimensions: {
                width: (_a = videoSourceStats === null || videoSourceStats === void 0 ? void 0 : videoSourceStats.width) !== null && _a !== void 0 ? _a : null,
                height: (_b = videoSourceStats === null || videoSourceStats === void 0 ? void 0 : videoSourceStats.height) !== null && _b !== void 0 ? _b : null,
            },
            captureFrameRate: (_c = videoSourceStats === null || videoSourceStats === void 0 ? void 0 : videoSourceStats.framesPerSecond) !== null && _c !== void 0 ? _c : null,
            dimensions: {
                width: upstreamObservableVideoMetrics.videoUpstreamFrameWidth,
                height: upstreamObservableVideoMetrics.videoUpstreamFrameHeight,
            },
            frameRate: upstreamObservableVideoMetrics.videoUpstreamFramesEncodedPerSecond,
            bytesSent: this.getTrackBytesTransmitted(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            bitRate: upstreamObservableVideoMetrics.videoUpstreamBitrate,
            packetsSent: upstreamObservableVideoMetrics.videoUpstreamPacketsSent,
            roundTripTime: upstreamObservableVideoMetrics.videoUpstreamRoundTripTimeMs,
            jitter: upstreamObservableVideoMetrics.videoUpstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM),
            packetLossPercentage: upstreamObservableVideoMetrics.videoUpstreamPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).videoCodec,
            ssrc: videoUpstreamSsrc,
            enabled: this.isLocalVideoTrackEnabled,
            trackId: '',
            trackSid: '',
        };
    }
    /* RemoteAudioTrackStats
     * -- audioLevel: number | null;
     * -- bytesReceived: number | null;
     * -- bitRate: number | null;
     * -- packetsReceived: number | null;
     * -- jitter: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     */
    buildRemoteAudioTrackStats(clientMetricReport, observableMetrics) {
        return {
            audioLevel: observableMetrics.audioDownstreamLevel,
            bytesReceived: this.getTrackBytesTransmitted(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            bitRate: this.getAudioTrackBitRate(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            packetsReceived: observableMetrics.audioPacketsReceived,
            jitter: observableMetrics.audioDownstreamJitterMs,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            packetLossPercentage: observableMetrics.audioPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).audioCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            trackId: '',
            trackSid: '',
        };
    }
    /* RemoteVideoTrackStats:
     * -- dimensions: VideoTrackDimensions | null;
     * -- frameRate: number | null;
     * -- bytesReceived: number | null;
     * -- bitRate: number | null;
     * -- packetsReceived: number | null;
     * -- timestamp: number;
     * -- packetsLost: number | null;
     * -- packetLossPercentage: number | null;
     * -- codec: string | null;
     * -- ssrc: string;
     * -- trackId: string;  // Might not be available in Chime
     * -- trackSid: string;  // Might not be available in Chime
     * -- jitter: number | null;
     */
    buildRemoteVideoTrackStats(clientMetricReport, observableVideoMetrics, attendeeId, logger) {
        var _a;
        const downstreamObservableVideoMetrics = (0, get_video_metrics_for_remote_user_1.getVideoMetricsForRemoteUser)(observableVideoMetrics, attendeeId, logger);
        if (!downstreamObservableVideoMetrics) {
            return null;
        }
        return {
            dimensions: {
                width: downstreamObservableVideoMetrics.videoDownstreamFrameWidth,
                height: downstreamObservableVideoMetrics.videoDownstreamFrameHeight,
            },
            frameRate: downstreamObservableVideoMetrics.videoDownstreamFramesDecodedPerSecond,
            bytesReceived: this.getTrackBytesTransmitted(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            bitRate: downstreamObservableVideoMetrics.videoDownstreamBitrate,
            packetsReceived: downstreamObservableVideoMetrics.videoDownstreamPacketsReceived,
            timestamp: clientMetricReport.currentTimestampMs,
            packetsLost: this.getTrackPacketsLost(clientMetricReport, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            packetLossPercentage: downstreamObservableVideoMetrics.videoDownstreamPacketLossPercent,
            codec: this.getCodecOrDefault(clientMetricReport.getRTCStatsReport()).videoCodec,
            ssrc: this.getStreamSsrc(clientMetricReport.streamMetricReports, amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO, amazon_chime_sdk_js_1.ClientMetricReportDirection.DOWNSTREAM),
            trackId: '',
            trackSid: '',
            jitter: downstreamObservableVideoMetrics.videoDownstreamJitterMs,
            jitterBufferDelay: (_a = downstreamObservableVideoMetrics.videoDownstreamJitterBufferDelay) !== null && _a !== void 0 ? _a : null,
        };
    }
    parseWebRtcStatsReportForVideoSourceStats(rtcStatsReport) {
        if (!rtcStatsReport) {
            return null;
        }
        let videoSourceStats = null;
        rtcStatsReport.forEach(stats => {
            if (stats.type === 'media-source' && stats.kind === 'video') {
                videoSourceStats = {
                    height: stats.height,
                    width: stats.width,
                    framesPerSecond: stats.framesPerSecond,
                };
            }
        });
        return videoSourceStats;
    }
    getTrackPacketsLost(clientMetricReport, mediaType, direction) {
        const streamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction && stream.mediaType === mediaType);
        if (!streamMetricReport) {
            return null;
        }
        return streamMetricReport.currentMetrics.packetsLost;
    }
    getTrackBytesTransmitted(clientMetricReport, mediaType, direction) {
        const streamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction && stream.mediaType === mediaType);
        if (!streamMetricReport) {
            return null;
        }
        return direction === amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM
            ? streamMetricReport.currentMetrics.bytesSent
            : streamMetricReport.currentMetrics.bytesReceived;
    }
    getAudioTrackBitRate(clientMetricReport, direction) {
        const audioStreamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === direction &&
            stream.mediaType === amazon_chime_sdk_js_1.ClientMetricReportMediaType.AUDIO);
        if (!audioStreamMetricReport) {
            return null;
        }
        const currentBytes = direction === amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM
            ? audioStreamMetricReport.currentMetrics.bytesSent
            : audioStreamMetricReport.currentMetrics.bytesReceived;
        const previousBytes = direction === amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM
            ? audioStreamMetricReport.previousMetrics.bytesSent
            : audioStreamMetricReport.previousMetrics.bytesReceived;
        return this.calculateBitrate(currentBytes, previousBytes);
    }
    calculateBitrate(currentBytes, previousBytes) {
        const bytesPerSecond = currentBytes - previousBytes;
        return bytesPerSecond * 8; // Convert to bits per second
    }
    getCodecOrDefault(rtcStatsReport) {
        let audioCodec = 'unknown';
        let videoCodec = 'unknown';
        if (!rtcStatsReport)
            return { audioCodec, videoCodec };
        rtcStatsReport.forEach(stats => {
            if (stats.type === 'codec') {
                const mimeType = stats.mimeType.toLowerCase();
                if (mimeType.includes('audio')) {
                    audioCodec = mimeType.split('/')[1];
                }
                else if (mimeType.includes('video')) {
                    videoCodec = mimeType.split('/')[1];
                }
            }
        });
        return { audioCodec, videoCodec };
    }
    getStreamSsrc(streamMetricReports, mediaType, direction) {
        for (const ssrc in streamMetricReports) {
            if (streamMetricReports[ssrc].mediaType === mediaType &&
                streamMetricReports[ssrc].direction === direction) {
                return ssrc;
            }
        }
        return '';
    }
    isVideoFrozen(clientMetricReport) {
        const localVideoStreamMetricReport = Object.values(clientMetricReport.streamMetricReports).find((stream) => stream.direction === amazon_chime_sdk_js_1.ClientMetricReportDirection.UPSTREAM &&
            stream.mediaType === amazon_chime_sdk_js_1.ClientMetricReportMediaType.VIDEO);
        if (!localVideoStreamMetricReport)
            return false;
        return (localVideoStreamMetricReport.currentMetrics.bytesSent > 0 &&
            localVideoStreamMetricReport.currentMetrics.frameRate < this.LOW_FRAME_RATE_THRESHOLD);
    }
}
exports.RoomMonitor = RoomMonitor;
//# sourceMappingURL=room_monitor.js.map